import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Alert,
  AlertIcon,
  Badge,
  Icon,
  useColorModeValue,
  Skeleton,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { FiMapPin, FiExternalLink, FiNavigation } from 'react-icons/fi';
import Map, { Marker, Source, Layer, MapRef } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

interface DistanceCalculatorProps {
  vendorAddress: string;
  vendorCity: string;
  vendorState: string;
  vendorPostalCode: string;
  vendorCountry: string;
}

const DistanceCalculator: React.FC<DistanceCalculatorProps> = ({
  vendorAddress,
  vendorCity,
  vendorState,
  vendorPostalCode,
  vendorCountry,
}) => {
  const [distance, setDistance] = useState<string>('');
  const [duration, setDuration] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [vendorCoords, setVendorCoords] = useState<[number, number] | null>(null);
  const [routeData, setRouteData] = useState<any>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  
  const mapRef = useRef<MapRef>(null);
  const mapBg = useColorModeValue('blue.50', 'blue.900');
  
  const TANGRAM_ADDRESS = '9200 Sorensen Avenue, Santa Fe Springs, CA 90670';
  const TANGRAM_COORDS: [number, number] = [-118.0951, 33.9425]; // Santa Fe Springs, CA coordinates

  // Mapbox access token
  const MAPBOX_TOKEN = 'pk.eyJ1IjoicHNtaXRoMDlwMTk4NyIsImEiOiJjazNnOWdsN2IwZGUzM2tvZnJyYmhzeDBjIn0.J93UxVsDqpWxKomujd9Nng';

  // Construct full vendor address
  const fullVendorAddress = [
    vendorAddress,
    vendorCity,
    vendorState && vendorPostalCode ? `${vendorState} ${vendorPostalCode}` : vendorState || vendorPostalCode,
    vendorCountry
  ].filter(Boolean).join(', ');

  const hasValidAddress = vendorAddress && vendorCity && (vendorState || vendorCountry);

  // Geocode vendor address to get coordinates
  const geocodeAddress = async (address: string): Promise<[number, number] | null> => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${MAPBOX_TOKEN}&limit=1`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        return [lng, lat];
      }
      return null;
    } catch (err) {
      console.error('Geocoding error:', err);
      return null;
    }
  };

  // Get route between two points
  const getRoute = async (start: [number, number], end: [number, number]) => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/driving/${start[0]},${start[1]};${end[0]},${end[1]}?geometries=geojson&access_token=${MAPBOX_TOKEN}`
      );
      const data = await response.json();
      
      if (data.routes && data.routes.length > 0) {
        const route = data.routes[0];
        return {
          geometry: route.geometry,
          distance: route.distance,
          duration: route.duration
        };
      }
      return null;
    } catch (err) {
      console.error('Routing error:', err);
      return null;
    }
  };

  // Calculate distance using Mapbox APIs
  const calculateDistance = async () => {
    if (!hasValidAddress) return;

    setLoading(true);
    setError('');

    try {
      // Geocode vendor address
      const coords = await geocodeAddress(fullVendorAddress);
      if (!coords) {
        throw new Error('Could not find location for the provided address');
      }
      
      setVendorCoords(coords);

      // Get route between vendor and Tangram
      const route = await getRoute(coords, TANGRAM_COORDS);
      if (!route) {
        throw new Error('Could not calculate route');
      }

      setRouteData(route);
      
      // Convert distance from meters to miles and format
      const distanceInMiles = Math.round(route.distance * 0.000621371);
      
      // Convert duration from seconds to a readable format
      const durationInMinutes = Math.round(route.duration / 60);
      const hours = Math.floor(durationInMinutes / 60);
      const minutes = durationInMinutes % 60;
      
      let durationText;
      if (hours === 0) {
        durationText = `${minutes} min`;
      } else if (minutes === 0) {
        durationText = `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
      } else {
        durationText = `${hours}h ${minutes}m`;
      }
      
      setDistance(`${distanceInMiles} miles`);
      setDuration(durationText);

      // Fit map to show both points
      if (mapRef.current) {
        const bounds = [
          [Math.min(coords[0], TANGRAM_COORDS[0]), Math.min(coords[1], TANGRAM_COORDS[1])],
          [Math.max(coords[0], TANGRAM_COORDS[0]), Math.max(coords[1], TANGRAM_COORDS[1])]
        ];
        
        mapRef.current.fitBounds(bounds as any, {
          padding: { top: 50, bottom: 50, left: 50, right: 50 }
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to calculate distance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasValidAddress && mapLoaded) {
      calculateDistance();
    }
  }, [vendorAddress, vendorCity, vendorState, vendorPostalCode, vendorCountry, mapLoaded]);

  // Generate Google Maps URL for route (backup)
  const getGoogleMapsUrl = () => {
    const origin = encodeURIComponent(fullVendorAddress);
    const destination = encodeURIComponent(TANGRAM_ADDRESS);
    return `https://www.google.com/maps/dir/${origin}/${destination}`;
  };

  if (!hasValidAddress) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Text fontSize="sm">
          Complete your company address in the Company Information step to see distance calculation to our warehouse.
        </Text>
      </Alert>
    );
  }

  const routeLayer: any = {
    id: 'route',
    type: 'line',
    layout: {
      'line-join': 'round',
      'line-cap': 'round'
    },
    paint: {
      'line-color': '#3182CE',
      'line-width': 4,
      'line-opacity': 0.8
    }
  };

  return (
    <Box bg={mapBg} p={6} borderRadius="lg" borderWidth={1}>
      <VStack spacing={4} align="stretch">
        <HStack spacing={3}>
          <Icon as={FiNavigation} color="blue.500" boxSize={5} />
          <Text fontSize="md" fontWeight="semibold">
            Distance to Tangram Warehouse
          </Text>
        </HStack>

        {/* Addresses */}
        <HStack justify="space-between" fontSize="sm">
          <VStack align="start" spacing={1} flex={1}>
            <Text fontWeight="medium" color="gray.700">From (Your Location):</Text>
            <Text color="gray.600">{fullVendorAddress}</Text>
          </VStack>
          <Icon as={FiMapPin} color="gray.400" mx={4} />
          <VStack align="end" spacing={1} flex={1}>
            <Text fontWeight="medium" color="gray.700">To (Tangram Warehouse):</Text>
            <Text color="gray.600" textAlign="right">{TANGRAM_ADDRESS}</Text>
          </VStack>
        </HStack>

        {/* Interactive Map */}
        <Box height="300px" borderRadius="md" overflow="hidden" position="relative">
          <Map
            ref={mapRef}
            mapboxAccessToken={MAPBOX_TOKEN}
            initialViewState={{
              longitude: -118.0951,
              latitude: 33.9425,
              zoom: 10
            }}
            style={{ width: '100%', height: '100%' }}
            mapStyle="mapbox://styles/psmith09p1987/clu8n5d6t02dg01oic0wnbhjj"
            onLoad={() => setMapLoaded(true)}
          >
            {/* Tangram Warehouse Marker */}
            <Marker longitude={TANGRAM_COORDS[0]} latitude={TANGRAM_COORDS[1]}>
              <Box
                bg="red.500"
                color="white"
                p={2}
                borderRadius="full"
                boxShadow="lg"
                border="2px solid white"
              >
                <Icon as={FiMapPin} boxSize={4} />
              </Box>
            </Marker>

            {/* Vendor Location Marker */}
            {vendorCoords && (
              <Marker longitude={vendorCoords[0]} latitude={vendorCoords[1]}>
                <Box
                  bg="blue.500"
                  color="white"
                  p={2}
                  borderRadius="full"
                  boxShadow="lg"
                  border="2px solid white"
                >
                  <Icon as={FiMapPin} boxSize={4} />
                </Box>
              </Marker>
            )}

            {/* Route Line */}
            {routeData && (
              <Source id="route" type="geojson" data={routeData.geometry}>
                <Layer {...routeLayer} />
              </Source>
            )}
          </Map>

          {/* Loading overlay */}
          {loading && (
            <Center
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg="rgba(255, 255, 255, 0.8)"
              backdropFilter="blur(4px)"
            >
              <VStack spacing={2}>
                <Spinner size="lg" color="blue.500" />
                <Text fontSize="sm" color="gray.600">Calculating route...</Text>
              </VStack>
            </Center>
          )}
        </Box>

        {/* Distance Results */}
        {error ? (
          <Alert status="warning" size="sm">
            <AlertIcon />
            <Text fontSize="sm">{error}</Text>
          </Alert>
        ) : distance && duration ? (
          <HStack justify="center" spacing={6} py={2}>
            <VStack spacing={1}>
              <Badge colorScheme="blue" variant="solid" px={3} py={1}>
                {distance}
              </Badge>
              <Text fontSize="xs" color="gray.600">Distance</Text>
            </VStack>
            <VStack spacing={1}>
              <Badge colorScheme="green" variant="solid" px={3} py={1}>
                {duration}
              </Badge>
              <Text fontSize="xs" color="gray.600">Driving Time</Text>
            </VStack>
          </HStack>
        ) : null}

        {/* Action Buttons */}
        <HStack spacing={3} justify="center">
          <Button
            size="sm"
            leftIcon={<Icon as={FiExternalLink} />}
            colorScheme="blue"
            variant="outline"
            onClick={() => window.open(getGoogleMapsUrl(), '_blank')}
          >
            View in Google Maps
          </Button>
          {!loading && hasValidAddress && (
            <Button
              size="sm"
              leftIcon={<Icon as={FiNavigation} />}
              colorScheme="green"
              variant="outline"
              onClick={calculateDistance}
              isLoading={loading}
            >
              Recalculate
            </Button>
          )}
        </HStack>

        <Text fontSize="xs" color="gray.500" textAlign="center">
          Interactive map shows actual driving route. Distance and time estimates are based on optimal driving conditions.
        </Text>
      </VStack>
    </Box>
  );
};

export default DistanceCalculator; 